import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useLang } from "@/contexts/LangContext";
import doctorFemale from "@/assets/showcase/doctor-tablet-teal.png";
import doctorMale from "@/assets/showcase/doctor-laptop-glasses.png";
import patient from "@/assets/showcase/patient-phone-smile.png";

const TEAL = "#0d5a5a";
const CORAL = "#ff8a6b";

export default function BottomInviteCta() {
  const { lang } = useLang();
  const isBn = lang === "bn";
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setVisible(true);
            io.disconnect();
          }
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -10% 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      className="px-4 sm:px-6 pt-12 sm:pt-16 pb-16 sm:pb-20 md:pb-24"
    >
      <div
        className={`relative max-w-6xl mx-auto rounded-[24px] sm:rounded-[32px] overflow-hidden transform-gpu transition-all duration-[900ms] ease-out motion-reduce:transition-none ${
          visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
        }`}
        style={{ background: "#eef4f3" }}
      >
        {/* Decorative accents */}
        <span
          className="hidden md:block absolute left-[28%] top-[52%] h-0 w-0"
          style={{
            borderLeft: "10px solid transparent",
            borderRight: "10px solid transparent",
            borderBottom: `18px solid ${CORAL}`,
            transform: "rotate(35deg)",
          }}
          aria-hidden
        />
        <span
          className="hidden md:block absolute right-[28%] top-[48%] h-2 w-2 rounded-full"
          style={{ background: TEAL, opacity: 0.55 }}
          aria-hidden
        />
        <span
          className="hidden md:block absolute right-[26%] top-[54%] h-2.5 w-2.5"
          style={{ background: CORAL, opacity: 0.75, transform: "rotate(20deg)" }}
          aria-hidden
        />

        <div className="relative z-10 pt-10 sm:pt-14 md:pt-20 px-5 sm:px-6 text-center">
          <h2
            className="text-[24px] sm:text-3xl md:text-5xl font-bold tracking-tight leading-[1.15] max-w-3xl mx-auto"
            style={{ color: TEAL, fontFamily: "'Plus Jakarta Sans','Inter',sans-serif" }}
          >
            {isBn
              ? "কম ঝামেলায়, বেশি নিয়ন্ত্রণে চালান আপনার প্র্যাকটিস"
              : "Run your practice with less admin and more control"}
          </h2>
          <p className="mt-4 sm:mt-5 text-[14.5px] sm:text-base md:text-lg max-w-2xl mx-auto leading-relaxed" style={{ color: "#3f6b6b" }}>
            {isBn
              ? "আপনি রোগী দেখতে শুরু করেছেন, রাত জেগে নোট আর পেমেন্টের পিছনে দৌড়াতে নয়। DoctorsX আপনাকে দিন, রোগী আর ব্যবসা — সব একসাথে সামলাতে সাহায্য করে।"
              : "You didn't start your practice to spend evenings on notes and chasing payments. DoctorsX keeps you on top of your day, your patients and your business — without burning out."}
          </p>
          <div className="mt-6 sm:mt-7 flex flex-col sm:flex-row sm:flex-wrap justify-center items-stretch sm:items-center gap-3 max-w-sm sm:max-w-none mx-auto">
            <Link
              to="/auth"
              className="inline-flex items-center justify-center rounded-full px-6 py-3 text-sm font-semibold shadow-sm hover:shadow-md transition"
              style={{ background: CORAL, color: "#0f172a" }}
            >
              {isBn ? "ডেমো নিন" : "Request a demo"}
            </Link>
            <Link
              to="/features"
              className="inline-flex items-center justify-center rounded-full px-6 py-3 text-sm font-semibold border transition hover:bg-white"
              style={{ color: TEAL, borderColor: TEAL }}
            >
              {isBn ? "নিজে ঘুরে দেখুন" : "Take a self-guided tour"}
            </Link>
          </div>
        </div>

        {/* Character row */}
        <div className="relative mt-8 md:mt-4 h-[180px] xs:h-[210px] sm:h-[260px] md:h-[300px]">
          <img
            src={doctorFemale}
            alt=""
            loading="lazy"
            aria-hidden
            className="absolute bottom-0 left-1 sm:left-2 md:left-6 h-[140px] sm:h-[220px] md:h-[300px] w-auto object-contain object-bottom select-none pointer-events-none"
            draggable={false}
          />
          <img
            src={patient}
            alt=""
            loading="lazy"
            aria-hidden
            className="absolute bottom-0 left-1/2 -translate-x-1/2 h-[170px] sm:h-[250px] md:h-[320px] w-auto object-contain object-bottom select-none pointer-events-none"
            draggable={false}
          />
          <img
            src={doctorMale}
            alt=""
            loading="lazy"
            aria-hidden
            className="absolute bottom-0 right-1 sm:right-2 md:right-6 h-[140px] sm:h-[220px] md:h-[300px] w-auto object-contain object-bottom select-none pointer-events-none"
            draggable={false}
          />
        </div>
      </div>
    </section>
  );
}
